import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { jobsAPI } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Clock,
  MapPin,
  User,
  Building2,
  Calendar,
  FileText,
  Edit,
  Trash2,
  DollarSign,
  Timer,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { hasAdminUI, canManageDivision, canMarkComplete } from '../lib/permissions';

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [jobHours, setJobHours] = useState(null);

  const isAdmin = hasAdminUI(user);
  const canEditThisJob = canManageDivision(user, job?.cost_center);
  const canCompleteThisJob = canMarkComplete(user, job);

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const response = await jobsAPI.get(id);
        setJob(response.data);
      } catch (error) {
        console.error('Failed to fetch job:', error);
        toast.error('Failed to load job details');
        navigate('/jobs');
      } finally {
        setLoading(false);
      }
    };

    const fetchJobHours = async () => {
      try {
        const response = await api.get(`/time-tracking/job/${id}/hours`);
        setJobHours(response.data);
      } catch (error) {
        // Not critical if this fails (might be non-admin or no entries)
        console.log('Could not fetch job hours:', error);
      }
    };

    fetchJob();
    if (isAdmin) {
      fetchJobHours();
    }
  }, [id, navigate, isAdmin]);

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await jobsAPI.update(id, { status: newStatus });
      setJob(prev => ({ ...prev, status: newStatus }));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDelete = async () => {
    try {
      await jobsAPI.delete(id);
      toast.success('Job deleted');
      navigate('/jobs');
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'status-pending',
      in_progress: 'status-in_progress',
      completed: 'status-completed',
      cancelled: 'status-cancelled',
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-3xl">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="job-detail">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold">{job.title}</h1>
            <div className="flex items-center gap-3 mt-2">
              {job.job_number && (
                <span className="text-xs font-mono bg-muted px-2 py-1 rounded" data-testid="job-detail-number">
                  {job.job_number}
                </span>
              )}
              <Badge className={`badge-status ${getStatusBadge(job.status)}`}>
                {job.status.replace('_', ' ')}
              </Badge>
              {job.customer_name && (
                <span className="text-muted-foreground flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  {job.customer_name}
                </span>
              )}
            </div>
          </div>
        </div>
        {canEditThisJob && (
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to={`/jobs/${id}/edit`}>
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Link>
            </Button>
            <Button variant="destructive" onClick={() => setShowDelete(true)}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        )}
      </div>

      {/* Details Card */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Job Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status Update */}
          {canEditThisJob ? (
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted">
              <span className="font-medium">Update Status</span>
              <Select
                value={job.status}
                onValueChange={handleStatusChange}
                disabled={updatingStatus}
              >
                <SelectTrigger className="w-40" data-testid="status-update-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : canCompleteThisJob ? (
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted">
              <span className="font-medium">Mark this job complete</span>
              <Button
                variant="default"
                disabled={updatingStatus}
                onClick={() => handleStatusChange('completed')}
                data-testid="supervisor-complete-btn"
              >
                Mark Complete
              </Button>
            </div>
          ) : null}

          {/* Description */}
          {job.description && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Description</h3>
              <p>{job.description}</p>
            </div>
          )}

          {/* Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="font-medium">
                    {format(parseISO(job.start_time), 'EEEE, MMMM d, yyyy')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Time</p>
                  <p className="font-medium">
                    {format(parseISO(job.start_time), 'h:mm a')} - {format(parseISO(job.end_time), 'h:mm a')}
                  </p>
                </div>
              </div>

              {job.location && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Location</p>
                    <p className="font-medium">{job.location}</p>
                  </div>
                </div>
              )}

              {job.cost_center && (
                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Division</p>
                    <p className="font-medium">{job.cost_center}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {job.customer_name && (
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Customer</p>
                    <p className="font-medium">{job.customer_name}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Assigned To</p>
                  {job.assigned_names?.length > 0 ? (
                    <div className="space-y-1">
                      {job.assigned_names.map((name, i) => (
                        <p key={`${name}-${i}`} className="font-medium">{name}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">Unassigned</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {job.notes && (
            <div className="pt-4 border-t">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p className="mt-1">{job.notes}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hours Worked by Employee (Admin only) */}
      {isAdmin && jobHours && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Timer className="w-5 h-5 text-primary" />
              Hours Worked
              <Badge variant="secondary" className="ml-2">
                {jobHours.total_hours}h total
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {jobHours.employees?.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No time entries logged yet</p>
                <Link to="/time-tracking" className="text-primary hover:underline text-sm mt-1 inline-block">
                  Add time entries →
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Job Duration Info */}
                <div className="flex items-center justify-between text-sm text-muted-foreground pb-3 border-b">
                  <span>Job Duration: {jobHours.job_duration_days} day(s)</span>
                  <span>Status: <Badge variant="outline" className="ml-1">{jobHours.job_status?.replace('_', ' ')}</Badge></span>
                </div>
                
                {/* Employee Hours Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 font-medium">Employee</th>
                        <th className="text-right py-2 font-medium">Days Worked</th>
                        <th className="text-right py-2 font-medium">Total Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                  {jobHours.employees.map((emp) => (
                        <tr key={emp.employee_id} className="border-b last:border-0" data-testid={`job-hours-${emp.employee_id}`}>
                          <td className="py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <User className="w-4 h-4 text-primary" />
                              </div>
                              <span className="font-medium">{emp.employee_name}</span>
                            </div>
                          </td>
                          <td className="py-3 text-right text-muted-foreground">
                            {emp.days_worked} / {jobHours.job_duration_days}
                          </td>
                          <td className="py-3 text-right">
                            <Badge variant={emp.total_hours > 0 ? "default" : "secondary"}>
                              {emp.total_hours}h
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Link to Time Tracking */}
                <div className="pt-3 border-t">
                  <Link 
                    to="/time-tracking" 
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    Manage time entries →
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Job</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{job.title}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
